import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Paper,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { LabelPrintCommand } from '../../../../models/labels';

interface ILabelPreviewDialogProps {
  opened: boolean;
  setOpened: (value: boolean) => void;
  callback: (labelPrintCommand: LabelPrintCommand) => void;
  labelPrintCommand: LabelPrintCommand;
  setLabelPrintCommand: (value: LabelPrintCommand) => void;
}

const LabelPreviewDialog: React.FC<any> = (props: ILabelPreviewDialogProps) => {
  const { opened, callback, setOpened, labelPrintCommand, setLabelPrintCommand } = props;

  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const handleDialogClose = () => {
    setOpened(false); // Use the prop.
  };

  const title = t('f-settings-labels');
  const btnText = t('f-vets-print');

  // The Health Injector firmware writes these values to the NFC sticker, which
  // only fits 20 characters per field. Restrict input and warn on longer values.
  const NFC_MAX_LENGTH = 20;
  const exceedsNfcLength = (value?: string) => (value?.length ?? 0) > NFC_MAX_LENGTH;
  const showNfcLengthWarning =
    exceedsNfcLength(labelPrintCommand?.medicalSupply) ||
    exceedsNfcLength(labelPrintCommand?.lotNumber) ||
    exceedsNfcLength(labelPrintCommand?.treatNumber);

  const [errors, setErrors] = useState({
    daysToUse: false
  });

  const printBtnClick = () => {
    if (labelPrintCommand?.daysToUse.length === 0 || parseInt(labelPrintCommand.daysToUse) <= 0) {
      setErrors({
        daysToUse: true
      });
      return;
    }

    if (isNaN(Number(labelPrintCommand?.daysToUse))) {
      setErrors({
        daysToUse: true
      });
    } else {
      setErrors({
        daysToUse: false
      });

      handleDialogClose();
      callback(labelPrintCommand);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={opened} fullScreen={fullScreen} onClose={handleDialogClose} sx={V4_DIALOG_MD}>
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box sx={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => handleDialogClose()}
                  aria-label="close"
                  sx={V4_ICON_BUTTON}
                >
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">{title}</Typography>
              </Box>

              <Button
                autoFocus
                color="inherit"
                id="printBtn"
                startIcon={<Icon>{'print'}</Icon>}
                onClick={() => printBtnClick()}
              >
                {btnText}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Box sx={{ mt: 2 }}>
          {showNfcLengthWarning && (
            <Box sx={{ mt: -1, mb: 3 }}>
              <Alert severity="warning">{t('f-label-nfc-length-warning')}</Alert>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="medicalSupply"
                    label={t('f-supp-name')}
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    disabled={true}
                    value={labelPrintCommand?.medicalSupply}
                    error={exceedsNfcLength(labelPrintCommand?.medicalSupply)}
                    slotProps={{ htmlInput: { maxLength: NFC_MAX_LENGTH } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="orderNumber"
                    label={t('f-supp-order-number')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={true}
                    value={labelPrintCommand?.orderNumber}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="animalType"
                    label={t('f-com-animal-type')}
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    disabled
                    value={labelPrintCommand?.animalType}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="size"
                    label={t('f-size')}
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    disabled={true}
                    value={labelPrintCommand?.size}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="treatmentNumber"
                    label={t('f-strat-treat')}
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    value={labelPrintCommand?.treatNumber}
                    error={exceedsNfcLength(labelPrintCommand?.treatNumber)}
                    slotProps={{ htmlInput: { maxLength: NFC_MAX_LENGTH } }}
                    onChange={(e) => setLabelPrintCommand({ ...labelPrintCommand, treatNumber: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="withdrawPeriod"
                    label={t('f-com-withdrawal')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled
                    value={labelPrintCommand?.withdrawal}
                  />
                </Grid>

                {/*<Grid item xs={12} sm={6} md={6}>*/}
                {/*	<TextField id="regNumber"*/}
                {/*						 label={t('f-reg-number')}*/}
                {/*						 variant="outlined"*/}
                {/*						 size="small"*/}
                {/*						 fullWidth*/}
                {/*						 value={labelData?.regNumber || ""}*/}
                {/*						 onChange={(e) => setLabelData({...labelData, regNumber: e.target.value})}/>*/}
                {/*</Grid>*/}

                {/*<Grid item xs={12} sm={6} md={6}>*/}
                {/*	<TextField id="dose"*/}
                {/*						 label={t('f-dose')}*/}
                {/*						 variant="outlined"*/}
                {/*						 size="small"*/}
                {/*						 fullWidth*/}
                {/*						 value={labelData?.dose || ""}*/}
                {/*						 onChange={(e) => setLabelData({...labelData, dose: e.target.value})}/>*/}
                {/*</Grid>*/}

                <Grid item xs={12} sm={6} md={6}>
                  {/* Already translated when the print command is built, so it matches the printed label. */}
                  <TextField
                    id="supplyMethod"
                    label={t('f-supplies-method')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled
                    value={labelPrintCommand?.method}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="lotNumber"
                    label={t('f-com-lot')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={labelPrintCommand?.lotNumber}
                    error={exceedsNfcLength(labelPrintCommand?.lotNumber)}
                    slotProps={{ htmlInput: { maxLength: NFC_MAX_LENGTH } }}
                    onChange={(e) => setLabelPrintCommand({ ...labelPrintCommand, lotNumber: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    id="days"
                    label={t('f-days')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={labelPrintCommand?.daysToUse}
                    error={errors.daysToUse}
                    onChange={(e) => setLabelPrintCommand({ ...labelPrintCommand, daysToUse: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item md={6}>
              <Paper elevation={1} style={{ maxWidth: 400 }}>
                <Box sx={{ p: 4 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.vetName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.date}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.vetAddress}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.vetPhone}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>{`Veterinary: ${labelPrintCommand?.vetContactName}`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.farmNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{`${labelPrintCommand?.farmName} ${labelPrintCommand?.size} ml`}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>{labelPrintCommand?.farmAddress}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.medicalSupply}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.size}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{`Animal: ${labelPrintCommand?.animalType}`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{labelPrintCommand?.method}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        {`Withdrawal period (${labelPrintCommand?.withdrawalFor}): ${labelPrintCommand?.withdrawal}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>{`Dosage: once a day for ${labelPrintCommand?.daysToUse} day(s).`}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {!fullScreen && (
        <DialogActions>
          <Button onClick={() => handleDialogClose()} startIcon={<Icon>{'close'}</Icon>} color="primary">
            {t('f-com-cancel')}
          </Button>

          <Button id="saveBtn" startIcon={<Icon>{'print'}</Icon>} color="primary" onClick={() => printBtnClick()}>
            {btnText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default LabelPreviewDialog;
